import React, { useState } from 'react';
import {
  Box,
  Alert,
  ButtonBase,
  CircularProgress,
  Collapse,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CheckCircleRounded as CheckCircleRoundedIcon,
  ChevronRightRounded as ChevronRightRoundedIcon,
  ErrorOutlineRounded as ErrorOutlineRoundedIcon,
} from '@mui/icons-material';
import colors from '../../assets/theme/colors';
import {
  formatCareSourceTimelineDateSummary,
  getCareSourceDisplayStatus,
  getCareSourceRecordedAtMillis,
  getCareSourceReviewTarget,
  getCareSourceSavedRecordCount,
  getCareSourceTimelinePointerCount,
  getCareSourceActivityTitle,
} from './careSourceStatusModel';
import CareSourceStatusRow from './CareSourceStatusRow';
import CareDocumentDetailSheet from './CareDocumentDetailSheet';
import MedicationUploadedSourceCard from './MedicationUploadedSourceCard';
import CaptureDestinationReceipt from './CaptureDestinationReceipt';
import EmailOrganizationReceipt from './EmailOrganizationReceipt';
import { useRecentCareSources } from './useRecentCareSources';
import { isComplexDirectProfileKnowledgeSource } from './directProfileKnowledgeModel';
import {
  getDirectProfileReviewItems,
} from './DirectProfileOrganizationReview';
import ProfileCareSourceReviewGroup from './ProfileCareSourceReviewGroup';
import {
  PROFILE_REVIEW_REQUEST_EVENT,
} from './ProfileReviewQueue';
import { buildExtractionResultSummary, getTouchedCategoryLabels } from './extractionResultSummaryModel';
import AppointmentCaptureScheduleAction from '../appointments/AppointmentCaptureScheduleAction';
import { useManualReviewQueueModel } from './useManualReviewQueueModel';
import {
  buildManualReviewQueueSummary,
  groupCareSourcesByCapture,
} from './profileReviewQueueModel';
import RecentActivitySummaryPanel, {
  formatCaptureTimestamp,
} from './RecentActivitySummaryPanel';
import {
  CAPTURE_DESTINATIONS,
  getCaptureDestinationMessage,
} from './captureDestinationPolicy';

// ---------------------------------------------------------------------------
// Data helpers (state derivation — no rendering)
// ---------------------------------------------------------------------------

const getSourceBucket = (source = {}) => {
  const label = getCareSourceDisplayStatus(source).label;
  if (label === 'Processing' || label === 'Reading text') return 'in_progress';
  if (label === 'Needs your review' || label === 'Failed') return 'live';
  return 'all_saved';
};

const isSourceSaved = (source = {}) => {
  if (getCareSourceSavedRecordCount(source) > 0) return true;
  return getSourceBucket(source) === 'all_saved';
};

const getOrderedCategoryLabels = (destinations = {}) => (
  Object.entries(destinations)
    .filter(([, count]) => Number(count) > 0)
    .map(([label]) => label)
);

const buildActivityCounts = (sources = [], manualReviewSummary = {}) => {
  const counts = sources.reduce((nextCounts, source) => {
    const bucket = getSourceBucket(source);
    const displayStatus = getCareSourceDisplayStatus(source);
    const careKnowledgeItemCount = isComplexDirectProfileKnowledgeSource(source)
      ? getDirectProfileReviewItems(source, source.suggestedCareKnowledgeItems || []).length
      : Number(
        source.careKnowledgeSuggestionSummary?.itemCount
        || source.suggestedCareKnowledgeItems?.length
        || 0
      );
    const profileSuggestionCount = Number(source.profileSuggestionSummary?.suggestionCount || 0);
    const extractionSummary = buildExtractionResultSummary(source);
    const touchedCategoryLabels = getTouchedCategoryLabels(source);

    nextCounts[bucket] += 1;
    if (isSourceSaved(source) && bucket !== 'all_saved') nextCounts.all_saved += 1;
    nextCounts.savedRecords += getCareSourceSavedRecordCount(source);
    nextCounts.timelinePointers += getCareSourceTimelinePointerCount(source);

    if (displayStatus.label === 'Needs your review') {
      nextCounts.needsReview += 1;
      nextCounts.careDetails += careKnowledgeItemCount + profileSuggestionCount;
    }
    nextCounts.extractedDetails += extractionSummary.total;
    nextCounts.automaticSaved += extractionSummary.automaticSaved;
    nextCounts.alreadyKnown += extractionSummary.alreadyKnown;
    nextCounts.unableToClassify += extractionSummary.unableToClassify;
    touchedCategoryLabels.forEach((label) => {
      if (!nextCounts.touchedCategoryLabels.includes(label)) {
        nextCounts.touchedCategoryLabels.push(label);
      }
    });

    if (bucket !== 'in_progress') nextCounts.processed += 1;
    return nextCounts;
  }, {
    live: 0,
    in_progress: 0,
    needsReview: 0,
    all_saved: 0,
    savedRecords: 0,
    timelinePointers: 0,
    careDetails: 0,
    profileSuggestions: 0,
    extractedDetails: 0,
    pendingReview: 0,
    automaticSaved: 0,
    alreadyKnown: 0,
    unableToClassify: 0,
    touchedCategoryLabels: [],
    pendingCategoryLabels: [],
    destinations: {
      Profile: 0,
      Goals: 0,
      Medications: 0,
      Allergies: 0,
    },
    processed: 0,
  });
  counts.pendingReview = Number(manualReviewSummary.pendingTotal || 0);
  counts.destinations = { ...(manualReviewSummary.destinations || {}) };
  counts.pendingCategoryLabels = getOrderedCategoryLabels(manualReviewSummary.destinations || {});
  counts.profileSuggestions = counts.pendingReview;
  return counts;
};

const getTimelineDateSummaryLabel = (sources = []) => {
  const labels = sources.map((s) => formatCareSourceTimelineDateSummary(s)).filter(Boolean);
  return labels[0] || '';
};

const formatDestinationSummary = (destinations = {}) => (
  Object.entries(destinations)
  .filter(([, count]) => count > 0)
  .map(([destination, count]) => getCaptureDestinationMessage(destination, count))
  .join(' '));

const formatDocumentDestinationSummary = (destinations = {}) => (
  Object.entries(destinations)
    .filter(([, count]) => count > 0)
    .map(([destination, count]) => {
      if (destination === 'Timeline') {
        return `Timeline ${count} item${count === 1 ? '' : 's'}`;
      }
      if (destination === 'Goals') {
        return `Goals ${count} goal${count === 1 ? '' : 's'}`;
      }
      if (destination === 'Allergies') {
        return `Allergies ${count} allerg${count === 1 ? 'y' : 'ies'}`;
      }
      if (destination === 'Document') {
        return `Document ${count} section${count === 1 ? '' : 's'}`;
      }
      return `${destination} ${count}`;
    })
    .join(' · ')
);

const formatCategoryList = (labels = [], separator = ' · ') => labels.filter(Boolean).join(separator);

export const getRecentActivityResultLabel = (activityCounts = {}) => {
  const pendingReview = Number(activityCounts.pendingReview || 0);
  const pendingCategoryLabels = Array.isArray(activityCounts.pendingCategoryLabels)
    ? activityCounts.pendingCategoryLabels
    : [];
  const touchedCategoryLabels = Array.isArray(activityCounts.touchedCategoryLabels)
    ? activityCounts.touchedCategoryLabels
    : [];

  if (pendingReview > 0) {
    const labelList = formatCategoryList(
      pendingCategoryLabels.length ? pendingCategoryLabels : touchedCategoryLabels,
      ', '
    );
    return labelList ? `Ready for review: ${labelList}` : `Review ${pendingReview} new detail${pendingReview === 1 ? '' : 's'}`;
  }

  if (touchedCategoryLabels.length > 0) {
    return `Added to ${formatCategoryList(touchedCategoryLabels, ', ')}`;
  }

  if (
    Number(activityCounts.savedRecords || 0)
    + Number(activityCounts.automaticSaved || 0)
    + Number(activityCounts.extractedDetails || 0) > 0
  ) {
    return 'Everything from this note was saved automatically';
  }

  if (Number(activityCounts.processed || 0) > 0) {
    return 'No new details found';
  }

  return '';
};

export const getRecentActivityResultTone = (activityCounts = {}) => {
  if (Number(activityCounts.pendingReview || 0) > 0) return 'attention';
  if (Array.isArray(activityCounts.touchedCategoryLabels) && activityCounts.touchedCategoryLabels.length > 0) return 'success';
  if (
    Number(activityCounts.savedRecords || 0)
    + Number(activityCounts.automaticSaved || 0) > 0
  ) return 'success';
  return 'secondary';
};

/**
 * Returns "today at 8:14 PM", "Jul 10 at 3:22 PM", etc.
 * Uses the most recent capturedAt/createdAt across all sources.
 */
const formatSavedAtLabel = (sources = []) => {
  const millis = sources.reduce((best, source) => {
    const ms = getCareSourceRecordedAtMillis(source);
    return ms > best ? ms : best;
  }, 0);

  if (!millis) return '';

  const date = new Date(millis);
  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();

  const timeStr = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);

  if (isToday) return `today at ${timeStr}`;

  const dateStr = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(date);

  return `${dateStr} at ${timeStr}`;
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Compact "Recent activity" card used in Timeline context.
 * Three states: processing → completed (no suggestions) → completed (suggestions).
 */
const RecentActivityCard = ({
  sources = [],
  activityKey = '',
  activityCounts = {},
  globalPendingReviewCount = 0,
  globalPendingSourceCount = 0,
  hideSectionLabel = false,
  childId = '',
  personName = '',
  profileReviewMode = false,
  onReviewTimeline,
  onApplyProfileSuggestions,
  onAddMedication,
  onEditMedication,
  onMedicationListChanged,
  onSetReminder,
  onProfileReviewComplete,
  onReviewAppointment,
  canManageAppointments,
  appointmentTimeZone,
}) => {
  const [open, setOpen] = useState(false);

  const isProcessing = activityCounts.in_progress > 0;
  const hasFailed = sources.some((s) => ['Failed', 'Partly processed'].includes(getCareSourceDisplayStatus(s).label));
  const hasProfileSuggestions = activityCounts.profileSuggestions > 0;
  const pendingReviewCount = activityCounts.pendingReview || 0;
  const hasSuggestions = pendingReviewCount > 0;
  const hasDocumentFindings = sources.some((source) => Boolean(
    source.documentFindings
    || source.metadata?.careSourceProcessing?.documentFindings
  ));
  const savedCount = activityCounts.savedRecords;
  const timelinePointerCount = activityCounts.timelinePointers || 0;
  const timelineActivityCount = savedCount + timelinePointerCount;
  const resolvedPersonName = personName || sources[0]?.childName || 'this person';
  const destinationSummary = formatDestinationSummary(activityCounts.destinations);
  const documentDestinationSummary = formatDocumentDestinationSummary(activityCounts.destinations);
  const touchedCategoryLabels = activityCounts.touchedCategoryLabels || [];
  const pendingCategoryLabels = activityCounts.pendingCategoryLabels || [];
  const categoryList = (labels = [], separator = ' · ') => labels.filter(Boolean).join(separator);
  const reviewCtaLabel = globalPendingReviewCount > pendingReviewCount
    ? `Review ${globalPendingReviewCount} item${globalPendingReviewCount === 1 ? '' : 's'} across ${globalPendingSourceCount} notes`
    : `Review ${pendingReviewCount} item${pendingReviewCount === 1 ? '' : 's'} from this note`;
  const savedAtLabel = formatSavedAtLabel(sources);
  const timelineDateSummaryLabel = getTimelineDateSummaryLabel(sources);
  const timelineDateSource = timelineDateSummaryLabel
    ? sources.find((s) => formatCareSourceTimelineDateSummary(s) === timelineDateSummaryLabel)
    : null;
  const timelineDateReviewTarget = timelineDateSource
    ? getCareSourceReviewTarget(timelineDateSource)
    : null;

  const handleSuggestionsClick = () => {
    if (hasProfileSuggestions) {
      if (typeof window === 'undefined') return;
      window.dispatchEvent(new CustomEvent(PROFILE_REVIEW_REQUEST_EVENT, {
        detail: { childId },
      }));
      return;
    }
    setOpen((current) => !current);
  };

  const handleTimelineDateClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (timelineDateReviewTarget) onReviewTimeline?.(timelineDateReviewTarget);
  };

  // Failure state: only use warning styling for an actual failed save
  if (hasFailed && !isProcessing) {
    return (
      <Paper
        data-testid={activityKey ? `recent-activity-${activityKey}` : undefined}
        elevation={0}
        sx={{
          borderRadius: '14px',
          border: `1px solid ${alpha('#B42318', 0.2)}`,
          bgcolor: alpha('#FFF5F5', 0.92),
          boxShadow: 'none',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ px: { xs: 1.25, sm: 1.5 }, py: { xs: 1.1, sm: 1.2 } }}>
          {!hideSectionLabel ? <Typography sx={{
            fontSize: '0.72rem',
            fontWeight: 700,
            color: alpha(colors.semantic.ink, 0.45),
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            mb: 0.75,
          }}>
            Recent activity
          </Typography> : null}
          <Stack direction="row" spacing={0.9} alignItems="flex-start">
            <ErrorOutlineRoundedIcon sx={{ fontSize: 17, color: '#B42318', mt: '1px' }} />
            <Box>
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: colors.semantic.ink, lineHeight: 1.3 }}>
                We couldn't organize your update
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: alpha(colors.semantic.ink, 0.6), mt: 0.2, lineHeight: 1.35 }}>
                Your original is saved. Open it to retry.
              </Typography>
            </Box>
          </Stack>
        </Box>
        <Collapse in={open} unmountOnExit>
          <Divider />
          <Box sx={{ px: { xs: 1.1, sm: 1.35 } }}>
            {sources.map((source, i) => (
              <React.Fragment key={source.id}>
                {i > 0 ? <Divider /> : null}
                <CareSourceStatusRow
                  source={source}
                  onReviewTimeline={onReviewTimeline}
                  onApplyProfileSuggestions={onApplyProfileSuggestions}
                  onAddMedication={onAddMedication}
                  onEditMedication={onEditMedication}
                  onMedicationListChanged={onMedicationListChanged}
                  onSetReminder={onSetReminder}
                  showDismiss
                  careKnowledgeReviewMode="link"
                />
              </React.Fragment>
            ))}
          </Box>
        </Collapse>
        <ButtonBase
          onClick={() => setOpen((v) => !v)}
          sx={{
            width: '100%',
            px: { xs: 1.25, sm: 1.5 },
            py: 0.85,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 0.5,
            borderTop: `1px solid ${alpha('#B42318', 0.1)}`,
          }}
        >
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#B42318' }}>
            {open ? 'Hide details' : 'View details'}
          </Typography>
          <ChevronRightRoundedIcon
            sx={{
              fontSize: 16,
              color: '#B42318',
              transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 150ms ease',
            }}
          />
        </ButtonBase>
      </Paper>
    );
  }

  return (
    <Paper
      data-testid={activityKey ? `recent-activity-${activityKey}` : undefined}
      elevation={0}
      sx={{
        borderRadius: '14px',
        border: `1px solid ${alpha(colors.semantic.line, 0.9)}`,
        bgcolor: '#FFFFFF',
        boxShadow: 'none',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: { xs: 1.25, sm: 1.5 }, py: { xs: 1.1, sm: 1.2 } }}>
        {/* Section label */}
        {!hideSectionLabel ? <Typography sx={{
          fontSize: '0.72rem',
          fontWeight: 700,
          color: alpha(colors.semantic.ink, 0.45),
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          mb: 0.75,
        }}>
          Recent activity
        </Typography> : null}

        {isProcessing ? (
          /* ── State 1: Processing ── */
          <Stack spacing={1.15}>
            <Stack direction="row" spacing={1.1} alignItems="flex-start">
              <CircularProgress
                size={15}
                thickness={5}
                sx={{ color: colors.semantic.sage, mt: '2px', flexShrink: 0 }}
              />
              <Box>
                <Typography sx={{
                  fontSize: '0.88rem',
                  fontWeight: 700,
                  color: colors.semantic.ink,
                  lineHeight: 1.3,
                }}>
                  Organizing your update…
                </Typography>
                <Typography sx={{
                  fontSize: '0.8rem',
                  color: alpha(colors.semantic.ink, 0.55),
                  mt: 0.2,
                  lineHeight: 1.35,
                }}>
                  You can keep using Lifelog while we finish.
                </Typography>
              </Box>
            </Stack>
            <Stack
              component="ul"
              spacing={0.55}
              sx={{
                m: 0,
                pl: 0,
                listStyle: 'none',
                color: alpha(colors.semantic.ink, 0.72),
              }}
            >
              {[
                ['✓', 'Original note saved'],
                ['⏳', 'Organizing Timeline'],
                ['⏳', 'Looking for long-term care details'],
                ['⏳', 'Checking for Goals, Medications, Allergies, and other structured information'],
              ].map(([symbol, label]) => (
                <Stack component="li" key={label} direction="row" spacing={0.75} alignItems="flex-start">
                  <Typography component="span" aria-hidden="true" sx={{ width: 18, fontSize: '0.78rem', lineHeight: 1.4 }}>
                    {symbol}
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.4 }}>
                    {label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            <Typography sx={{
              fontSize: '0.76rem',
              color: alpha(colors.semantic.ink, 0.52),
              lineHeight: 1.35,
            }}>
              This usually takes a few seconds.
            </Typography>
          </Stack>
        ) : hasSuggestions ? (
          /* ── State 3: Completed with suggestions ── */
          <Stack spacing={0.55}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <CheckCircleRoundedIcon sx={{ fontSize: 16, color: colors.semantic.sageDeep, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: colors.semantic.ink, lineHeight: 1.3 }}>
                {hasDocumentFindings ? 'Document details found' : 'Care note added'}
              </Typography>
            </Stack>

            <Typography sx={{
              fontSize: '0.8rem',
              color: alpha(colors.semantic.ink, 0.58),
              lineHeight: 1.35,
              pl: 3.1,
            }}>
              {hasDocumentFindings
                ? (pendingReviewCount > 0
                  ? `Ready for review: ${categoryList(pendingCategoryLabels.length ? pendingCategoryLabels : touchedCategoryLabels)}${documentDestinationSummary ? ` · ${documentDestinationSummary}` : ''}`
                  : `Everything from this note was saved automatically.${touchedCategoryLabels.length ? ` Saved to: ${categoryList(touchedCategoryLabels)}` : ''}`)
                : (pendingReviewCount > 0
                  ? `Ready for review: ${categoryList(pendingCategoryLabels.length ? pendingCategoryLabels : touchedCategoryLabels)}${destinationSummary ? ` · ${destinationSummary}` : ''}`
                  : `Everything from this note was saved automatically.${touchedCategoryLabels.length ? ` Saved to: ${categoryList(touchedCategoryLabels)}` : ''}`)}
            </Typography>
            {activityCounts.automaticSaved > 0 || activityCounts.alreadyKnown > 0 ? (
              <Typography sx={{ fontSize: '0.8rem', color: alpha(colors.semantic.ink, 0.58), lineHeight: 1.35, pl: 3.1 }}>
                {[
                  activityCounts.automaticSaved > 0 && getCaptureDestinationMessage(
                    CAPTURE_DESTINATIONS.CARE_KNOWLEDGE,
                    activityCounts.automaticSaved
                  ),
                  activityCounts.alreadyKnown > 0 && `${activityCounts.alreadyKnown} already known — no duplicate added`,
                ].filter(Boolean).join(' · ')}
              </Typography>
            ) : null}
            {timelineActivityCount > 0 ? (
              <Typography sx={{ fontSize: '0.8rem', color: alpha(colors.semantic.ink, 0.58), lineHeight: 1.35, pl: 3.1 }}>
                {`Saved to: ${categoryList(touchedCategoryLabels)}`}
              </Typography>
            ) : hasDocumentFindings ? (
              <Typography sx={{ fontSize: '0.8rem', color: alpha(colors.semantic.ink, 0.58), lineHeight: 1.35, pl: 3.1 }}>
                No Timeline items were added yet. Review the document findings to save what belongs on the Timeline, Goals, Allergies, or Document record.
              </Typography>
            ) : null}
            {hasProfileSuggestions ? (
              <Typography sx={{ fontSize: '0.8rem', color: alpha(colors.semantic.ink, 0.58), lineHeight: 1.35, pl: 3.1 }}>
                {`Review each item before saving it to ${resolvedPersonName}’s Profile or structured manager.`}
              </Typography>
            ) : null}

            {/* Tappable suggestion row */}
            <ButtonBase
              onClick={handleSuggestionsClick}
              aria-label={reviewCtaLabel}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 0.4,
                pl: 3.1,
                py: 0.25,
                borderRadius: '8px',
                mx: -0.5,
                px: 0.5,
                '&:hover': { bgcolor: alpha(colors.semantic.sageMist, 0.7) },
              }}
            >
              <Typography sx={{
                fontSize: '0.82rem',
                fontWeight: 700,
                color: colors.semantic.sageDeep,
                lineHeight: 1.3,
              }}>
                {reviewCtaLabel}
              </Typography>
              <ChevronRightRoundedIcon
                sx={{
                  fontSize: 16,
                  color: colors.semantic.sageDeep,
                  transform: !hasProfileSuggestions && open ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 150ms ease',
                }}
              />
            </ButtonBase>

            {savedAtLabel ? (
              <Typography sx={{
                fontSize: '0.78rem',
                color: alpha(colors.semantic.ink, 0.46),
                lineHeight: 1.3,
                pl: 3.1,
              }}>
                Last saved {savedAtLabel}
              </Typography>
            ) : null}
            {timelineDateSummaryLabel && timelineDateReviewTarget ? (
              <Typography
                component="span"
                onClick={handleTimelineDateClick}
                sx={{
                  display: 'block',
                  fontSize: '0.78rem',
                  color: colors.semantic.sageDeep,
                  cursor: 'pointer',
                  fontWeight: 700,
                  lineHeight: 1.3,
                  pl: 3.1,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {timelineDateSummaryLabel}
              </Typography>
            ) : null}
          </Stack>
        ) : (
          /* ── State 2: Completed, nothing to review ── */
          <Stack spacing={0.4}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <CheckCircleRoundedIcon sx={{ fontSize: 16, color: colors.semantic.sageDeep, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: colors.semantic.ink, lineHeight: 1.3 }}>
                Care note added
              </Typography>
            </Stack>
            {savedAtLabel ? (
              <Typography sx={{
                fontSize: '0.78rem',
                color: alpha(colors.semantic.ink, 0.46),
                lineHeight: 1.3,
                pl: 3.1,
              }}>
                Last saved {savedAtLabel}
              </Typography>
            ) : null}
            <Typography sx={{ fontSize: '0.8rem', color: alpha(colors.semantic.ink, 0.58), lineHeight: 1.35, pl: 3.1 }}>
              {activityCounts.touchedCategoryLabels.length > 0
                ? `Saved to: ${categoryList(activityCounts.touchedCategoryLabels)}`
                : 'Everything from this note was saved automatically.'}
            </Typography>
            {pendingReviewCount > 0 ? (
              <Typography sx={{ fontSize: '0.8rem', color: alpha(colors.semantic.ink, 0.58), lineHeight: 1.35, pl: 3.1 }}>
                {`Ready for review: ${categoryList(pendingCategoryLabels.length ? pendingCategoryLabels : touchedCategoryLabels)}`}
              </Typography>
            ) : null}
            <ButtonBase
              onClick={() => setOpen((current) => !current)}
              sx={{ alignSelf: 'flex-start', ml: 2.6, px: 0.5, py: 0.25, borderRadius: '8px' }}
            >
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: colors.semantic.sageDeep }}>
                {open ? 'Hide note' : 'View note'}
              </Typography>
            </ButtonBase>
          </Stack>
        )}
      </Box>

      {/* Detail expand — CareSourceStatusRow stays intact */}
      <Collapse in={open} unmountOnExit>
        <Divider />
        <Box sx={{ px: { xs: 1.1, sm: 1.35 } }}>
          {!isProcessing && !hasFailed ? <AppointmentCaptureScheduleAction sources={sources} canManage={canManageAppointments} timeZone={appointmentTimeZone} onReview={onReviewAppointment} /> : null}
          <CaptureDestinationReceipt
            sources={sources}
            childId={childId}
            captureKey={activityKey}
          />
          <EmailOrganizationReceipt sources={sources} childId={childId} />
          {sources.length ? <Divider /> : null}
          {sources.map((source, index) => (
            <React.Fragment key={source.id}>
              {index > 0 ? <Divider /> : null}
              <CareSourceStatusRow
                source={source}
                onReviewTimeline={onReviewTimeline}
                onApplyProfileSuggestions={onApplyProfileSuggestions}
                onAddMedication={onAddMedication}
                onEditMedication={onEditMedication}
                onMedicationListChanged={onMedicationListChanged}
                onSetReminder={onSetReminder}
                showDismiss
                careKnowledgeReviewMode={profileReviewMode ? 'inline' : 'link'}
                hideCareKnowledgeNavigation={profileReviewMode}
                onProfileReviewComplete={onProfileReviewComplete}
              />
            </React.Fragment>
          ))}
          {!sources.length ? (
            <Typography sx={{ py: 1.5, color: alpha(colors.semantic.ink, 0.62), fontSize: '0.85rem' }}>
              Nothing in this view right now.
            </Typography>
          ) : null}
        </Box>
      </Collapse>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Medication panel (unchanged visual design)
// ---------------------------------------------------------------------------

const MedicationSourcePanel = ({
  sources = [],
  title = '',
  onReviewTimeline,
  onApplyProfileSuggestions,
  onAddMedication,
  onEditMedication,
  onMedicationListChanged,
  onMedicationReviewComplete,
  onSetReminder,
  medicationReviewNavigation = null,
  existingMedications = [],
  onOpenMedicationReview,
}) => {
  const [inlineReviewSourceId, setInlineReviewSourceId] = useState('');
  const [originalSource, setOriginalSource] = useState(null);
  const orderedSources = [...sources].sort((left, right) => {
    const leftReviewed = getCareSourceDisplayStatus(left).label === 'Reviewed';
    const rightReviewed = getCareSourceDisplayStatus(right).label === 'Reviewed';
    return Number(leftReviewed) - Number(rightReviewed);
  });
  const handleMedicationReviewClick = (reviewSource) => {
    if (reviewSource && onOpenMedicationReview) {
      onOpenMedicationReview(reviewSource);
      return;
    }
    setInlineReviewSourceId((current) => current === reviewSource?.id ? '' : reviewSource?.id || '');
  };

  return (
    <Stack spacing={1.1} sx={{ mt: 0.25, mb: 1.2 }} aria-label={title || 'Medication source activity'}>
      {orderedSources.map((source) => (
        <React.Fragment key={source.id}>
          <MedicationUploadedSourceCard
            source={source}
            onReview={handleMedicationReviewClick}
            onViewOriginal={setOriginalSource}
          />
          <Collapse in={inlineReviewSourceId === source.id} unmountOnExit>
            <Box sx={{ px: { xs: 1.25, sm: 1.35 } }}>
              <CareSourceStatusRow
                source={source}
                existingMedications={existingMedications}
                onReviewTimeline={onReviewTimeline}
                onApplyProfileSuggestions={onApplyProfileSuggestions}
                onAddMedication={onAddMedication}
                onEditMedication={onEditMedication}
                onMedicationListChanged={onMedicationListChanged}
                onMedicationReviewComplete={onMedicationReviewComplete}
                onSetReminder={onSetReminder}
                medicationReviewNavigation={medicationReviewNavigation}
                showDismiss
                defaultReviewingMedications
                hideMedicationReviewToggle
              />
            </Box>
          </Collapse>
        </React.Fragment>
      ))}
      <CareDocumentDetailSheet
        open={Boolean(originalSource)}
        source={originalSource}
        onClose={() => setOriginalSource(null)}
      />
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

const CareSourceStatusPanel = ({
  childId = '',
  existingMedications = [],
  onReviewTimeline,
  onApplyProfileSuggestions,
  onAddMedication,
  onEditMedication,
  onMedicationListChanged,
  onMedicationReviewComplete,
  onSetReminder,
  medicationReviewNavigation = null,
  sourceContext = '',
  documentType = '',
  sourceKind = '',
  sourceType = '',
  activityFilter = null,
  includeAllMatching = false,
  notifyTimelineChanges = true,
  title = 'Timeline updates',
  profileReviewMode = false,
  personName = '',
  onProfileReviewComplete,
  initialOpenCaptureDetailsKey = '',
  initialOpenCaptureRequestKey = '',
  onOpenMedicationReview,
  onPendingReviewCountChange,
  onReviewAppointment,
  canManageAppointments = false,
  appointmentTimeZone = '',
}) => {
  const { error, sources } = useRecentCareSources({
    childId,
    sourceContext,
    documentType,
    sourceKind,
    sourceType,
    activityFilter,
    includeAllMatching,
    notifyTimelineChanges,
  });
  const manualReview = useManualReviewQueueModel(sources);
  const isMedicationSourcePanel = sourceContext === 'medication' || title === 'Medication source activity';
  const pendingMedicationReviewCount = isMedicationSourcePanel
    ? sources.reduce((total, source) => {
        if (getCareSourceDisplayStatus(source).label !== 'Needs your review') return total;
        return total + Math.max(1, Number(source.medicationSuggestionSummary?.suggestionCount || 0));
      }, 0)
    : Number(manualReview.summary.pendingTotal || 0);
  React.useEffect(() => {
    onPendingReviewCountChange?.(pendingMedicationReviewCount);
  }, [onPendingReviewCountChange, pendingMedicationReviewCount]);

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        We saved your source, but could not load its status right now.
      </Alert>
    );
  }

  if (!sources.length) return null;

  if (isMedicationSourcePanel) {
    return (
      <MedicationSourcePanel
        sources={sources}
        title={title}
        existingMedications={existingMedications}
        onReviewTimeline={onReviewTimeline}
        onApplyProfileSuggestions={onApplyProfileSuggestions}
        onAddMedication={onAddMedication}
        onEditMedication={onEditMedication}
        onMedicationListChanged={onMedicationListChanged}
        onMedicationReviewComplete={onMedicationReviewComplete}
        onSetReminder={onSetReminder}
        medicationReviewNavigation={medicationReviewNavigation}
        onOpenMedicationReview={onOpenMedicationReview}
        onProfileReviewComplete={onProfileReviewComplete}
      />
    );
  }

  if (profileReviewMode) {
    return (
      <Stack spacing={1} sx={{ mt: 0.25, mb: 1.2 }}>
        {sources.map((source) => (
          <ProfileCareSourceReviewGroup
            key={source.id}
            source={source}
            personName={personName}
            onProfileReviewComplete={onProfileReviewComplete}
          />
        ))}
      </Stack>
    );
  }

  const sourceGroups = groupCareSourcesByCapture(sources);
  const sourceGroupViewModels = sourceGroups.map((group) => {
    const reviewSummary = buildManualReviewQueueSummary(
      group.sources,
      manualReview.structuredDestinationsBySource
    );
    const recordedAtMillis = group.sources.reduce(
      (latest, source) => Math.max(latest, getCareSourceRecordedAtMillis(source)),
      0
    );
    return {
      ...group,
      reviewSummary,
      activityCounts: buildActivityCounts(group.sources, reviewSummary),
      recordedAtMillis,
      dateLabel: formatCaptureTimestamp(recordedAtMillis),
      title: getCareSourceActivityTitle(group.sources[0]),
    };
  });
  const pendingSourceCount = sourceGroupViewModels.filter(
    (group) => group.reviewSummary.pendingTotal > 0
  ).length;
  const today = new Date();
  const compactGroups = sourceGroupViewModels.map((group) => ({
    ...group,
    isProcessing: group.activityCounts.in_progress > 0,
    hasFailed: group.sources.some(
      (source) => ['Failed', 'Partly processed'].includes(getCareSourceDisplayStatus(source).label)
    ),
    isToday: Boolean(group.recordedAtMillis)
      && new Date(group.recordedAtMillis).toDateString() === today.toDateString(),
    needsReview: group.reviewSummary.pendingTotal > 0,
    resultLabel: getRecentActivityResultLabel(group.activityCounts),
    resultTone: getRecentActivityResultTone(group.activityCounts),
    renderDetails: () => (
      <RecentActivityCard
        activityKey={group.key}
        sources={group.sources}
        activityCounts={group.activityCounts}
        globalPendingReviewCount={manualReview.summary.pendingTotal}
        globalPendingSourceCount={pendingSourceCount}
        childId={childId}
        personName={personName}
        profileReviewMode={profileReviewMode}
        onReviewTimeline={onReviewTimeline}
        onApplyProfileSuggestions={onApplyProfileSuggestions}
        onAddMedication={onAddMedication}
        onEditMedication={onEditMedication}
        onMedicationListChanged={onMedicationListChanged}
        onSetReminder={onSetReminder}
        onProfileReviewComplete={onProfileReviewComplete}
        onReviewAppointment={onReviewAppointment}
        canManageAppointments={canManageAppointments}
        appointmentTimeZone={appointmentTimeZone}
        hideSectionLabel
      />
    ),
  }));

  return (
    <Box sx={{ mt: 0.25, mb: 1.2 }}>
      <RecentActivitySummaryPanel
        groups={compactGroups}
        initialOpenCaptureDetailsKey={initialOpenCaptureDetailsKey}
        initialOpenCaptureRequestKey={initialOpenCaptureRequestKey}
      />
    </Box>
  );
};

export default CareSourceStatusPanel;
